import * as THREE from "three";
import { CollisionWorld, type CollisionObb, type WalkSurfaceQuery } from "../collision";
import type { MovementResolution, MovementResolutionRequest } from "../core/types";
import type {
  ArchitectureCollisionShape,
  ArchitectureWalkableSurface,
} from "./ArchitectureLibrary";

interface WorldWalkableSurface extends CollisionObb {
  height: number;
  surface: "stone" | "wood";
}

export interface WorldSpatialRegistration {
  readonly object: THREE.Object3D;
  readonly prefix: string;
  collisionIds: string[];
  surfaceIds: string[];
}

export class WorldSpatialIndex {
  private readonly collision: CollisionWorld;
  private readonly surfaces = new Map<string, WorldWalkableSurface>();

  constructor(terrain: WalkSurfaceQuery) {
    this.collision = new CollisionWorld({ surface: terrain });
  }

  resolveMovement(request: MovementResolutionRequest): MovementResolution {
    return this.collision.resolveMovement(request);
  }

  register(object: THREE.Object3D, prefix: string): WorldSpatialRegistration {
    const registration: WorldSpatialRegistration = { object, prefix, collisionIds: [], surfaceIds: [] };
    this.refresh(registration);
    return registration;
  }

  refresh(registration: WorldSpatialRegistration): void {
    this.removeEntries(registration);
    registration.object.updateWorldMatrix(true, false);
    const idMap = new Map<string, string[]>();
    const usedIds = new Map<string, number>();

    registration.object.traverse((child) => {
      if (child !== registration.object && child.userData.template) return;
      const collisionShapes = (child.userData.collisionShapes ?? []) as ArchitectureCollisionShape[];
      const walkableSurfaces = (child.userData.walkableSurfaces ?? []) as ArchitectureWalkableSurface[];
      for (const shape of collisionShapes) {
        const id = uniqueId(`${registration.prefix}:${shape.id}`, usedIds);
        this.collision.add(this.transformShape(shape, id, child.matrixWorld));
        registration.collisionIds.push(id);
        idMap.set(shape.id, [...(idMap.get(shape.id) ?? []), id]);
      }
      for (const surface of walkableSurfaces) {
        const id = uniqueId(`${registration.prefix}:surface:${surface.id}`, usedIds);
        this.surfaces.set(id, this.transformSurface(surface, id, child.matrixWorld));
        registration.surfaceIds.push(id);
      }
    });

    registration.object.traverse((child) => {
      const localIds = child.userData.collisionShapeIds as string[] | undefined;
      if (localIds) child.userData.worldCollisionShapeIds = localIds.flatMap((id) => idMap.get(id) ?? []);
    });
  }

  unregister(registration: WorldSpatialRegistration): void {
    this.removeEntries(registration);
    registration.object.traverse((child) => delete child.userData.worldCollisionShapeIds);
  }

  setInteractionEnabled(object: THREE.Object3D, enabled: boolean): void {
    const ids = object.userData.worldCollisionShapeIds as string[] | undefined;
    for (const id of ids ?? []) this.collision.setEnabled(id, enabled);
  }

  getSurfaceAt(x: number, z: number): WorldWalkableSurface | null {
    let match: WorldWalkableSurface | null = null;
    for (const surface of this.surfaces.values()) {
      if (!containsPoint(surface, x, z)) continue;
      if (!match || surface.height > match.height) match = surface;
    }
    return match;
  }

  clear(): void {
    this.collision.clear();
    this.surfaces.clear();
  }

  private removeEntries(registration: WorldSpatialRegistration): void {
    for (const id of registration.collisionIds) this.collision.remove(id);
    for (const id of registration.surfaceIds) this.surfaces.delete(id);
    registration.collisionIds = [];
    registration.surfaceIds = [];
  }

  private transformShape(shape: ArchitectureCollisionShape, id: string, matrix: THREE.Matrix4): CollisionObb {
    const transformed = transformObb(shape, matrix);
    return { id, shape: "obb", ...transformed, enabled: shape.enabled };
  }

  private transformSurface(
    surface: ArchitectureWalkableSurface,
    id: string,
    matrix: THREE.Matrix4,
  ): WorldWalkableSurface {
    const transformed = transformObb(surface, matrix);
    const point = new THREE.Vector3(surface.centerX, surface.height, surface.centerZ).applyMatrix4(matrix);
    return { id, shape: "obb", ...transformed, height: point.y, surface: surface.surface };
  }
}

function transformObb(
  shape: Pick<ArchitectureCollisionShape, "centerX" | "centerZ" | "halfWidth" | "halfDepth" | "rotation">,
  matrix: THREE.Matrix4,
): Pick<CollisionObb, "centerX" | "centerZ" | "halfWidth" | "halfDepth" | "rotation"> {
  const center = new THREE.Vector3(shape.centerX, 0, shape.centerZ).applyMatrix4(matrix);
  const origin = new THREE.Vector3(0, 0, 0).applyMatrix4(matrix);
  const width = new THREE.Vector3(
    Math.cos(shape.rotation) * shape.halfWidth,
    0,
    Math.sin(shape.rotation) * shape.halfWidth,
  ).applyMatrix4(matrix).sub(origin);
  const depth = new THREE.Vector3(
    -Math.sin(shape.rotation) * shape.halfDepth,
    0,
    Math.cos(shape.rotation) * shape.halfDepth,
  ).applyMatrix4(matrix).sub(origin);
  return {
    centerX: center.x,
    centerZ: center.z,
    halfWidth: Math.hypot(width.x, width.z),
    halfDepth: Math.hypot(depth.x, depth.z),
    rotation: Math.atan2(width.z, width.x),
  };
}

function containsPoint(surface: WorldWalkableSurface, x: number, z: number): boolean {
  const dx = x - surface.centerX;
  const dz = z - surface.centerZ;
  const cos = Math.cos(surface.rotation);
  const sin = Math.sin(surface.rotation);
  const localX = dx * cos + dz * sin;
  const localZ = -dx * sin + dz * cos;
  return Math.abs(localX) <= surface.halfWidth && Math.abs(localZ) <= surface.halfDepth;
}

function uniqueId(base: string, used: Map<string, number>): string {
  const count = used.get(base) ?? 0;
  used.set(base, count + 1);
  return count === 0 ? base : `${base}-${count + 1}`;
}
